import { useEntityChange } from '@/common/entityChange/EntityChangeContext';
import { ContentBox } from '@/common/layout/ContentBox';
import { ScrollableSpacerLayoutArea } from '@/common/layout/ScrollableSpacerLayoutArea';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { faSave, faTimes } from '@awesome.me/kit-b4fd369ea4/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Form } from '../../form/Form';
import { EntityAuditCard } from '../../form/cards/EntityAuditCard';
import { EntitySectionCard } from '../../form/cards/EntitySectionCard';
import { EntityStatusCard } from '../../form/cards/EntityStatusCard';
import { useEntityFromRoute } from '../../hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '../../hooks/route/useModuleFromRoute';
import { useNavState } from '../../navState/NavStateContext';
import { usePrincipal } from '../../principal/PrincipalContext';
import { useRsrc } from '../../rsrc/RsrcContext';
import { EntityUtils } from '../../utils/EntityUtils';
import { ExprUtils } from '../../utils/ExprUtils';
import { useTab } from '../entityTabs/TabContext';
import { useEntityMgr } from '../entityView/EntityMgrContext';
import { EntityFormMode } from '../enumerations/EntityFormMode';
import { FieldDefsProvider } from './FieldDefsContext';

export const EntityForm = ({
  // Optional Module and Entity. If not provided they will be grabbed from the route
  module,
  entity,

  // Optional Entity ID. If not provided it will be retrieved from navState
  entityId: entityIdProp,

  // Optional Modifications to EntityForm
  augFieldDefs,
  children,
}) => {
  const { tabLabel } = useTab();

  // The "Free" EntityForm must either be housed in an Info tab, or as a standalone component without any tabs
  if (!(tabLabel === 'Info' || tabLabel === null) && !entity) {
    throw new Error(`An Entity needs to be provided to the EntityForm in tab ${tabLabel}. This is a development error`);
  }

  const routeModule = useModuleFromRoute();
  module ??= routeModule;
  const routeEntity = useEntityFromRoute();
  entity ??= routeEntity;

  const { navState, assign } = useNavState();
  const rsrc = useRsrc();
  const { asOfDate, entityFormMode, setEntityFormMode } = useEntityMgr();
  const { getFieldRoleSet } = usePrincipal();

  const [fieldDefs, setFieldDefs] = useState();
  const [entityId, setEntityId] = useState(entityIdProp ?? navState.entityId);

  const formRef = useRef();

  useEffect(() => {
    rsrc.fieldDefs(module, entity).then((fieldDefs) => {
      // TODO: Map field type to form field component

      if (augFieldDefs) {
        const fieldToFieldDef = Object.fromEntries(fieldDefs.map((fieldDef) => [fieldDef.field, fieldDef]));
        augFieldDefs(fieldToFieldDef);
        fieldDefs = Object.values(fieldToFieldDef);
      }

      fieldDefs = fieldDefs.filter((fieldDef) => !fieldDef.checkboxSelection);
      fieldDefs = EntityUtils.filterAuthorizedFields(module, entity, fieldDefs, getFieldRoleSet);

      fieldDefs.sort((a, b) => (a.precedence || 0) - (b.precedence || 0));

      setDisabled(fieldDefs);
      setFieldDefs(fieldDefs);
    });
  }, []);

  const { refreshTrigger, subscrToEntityChange, unsubscrToEntityChange } = useEntityChange();
  useEffect(() => {
    subscrToEntityChange(module, entity, [entityId]);
    return () => {
      unsubscrToEntityChange(module, entity, [entityId]);
    };
  }, [entityId]);

  // Responsible for setting the disabled status of fieldDefs
  const setDisabled = (fieldDefs) => {
    if (!fieldDefs) return;
    fieldDefs.forEach((fieldDef) => {
      if (entityFormMode === EntityFormMode.VIEW) {
        fieldDef.disabled = true;
      } else if (entityFormMode === EntityFormMode.ADD) {
        fieldDef.disabled = fieldDef.editLevel === 'NEVER';
      } else {
        fieldDef.disabled =
          fieldDef.editLevel === 'NEVER' ||
          fieldDef.editLevel === 'ADD_ONLY' ||
          (fieldDef.editLevel === 'SOR' && !formRef.current.formValues?.sor);
      }
    });
  };

  // Subscribe to the mode
  useEffect(() => {
    if (!fieldDefs) return;
    setDisabled(fieldDefs);
    setFieldDefs([...fieldDefs]); // Putting the fieldDefs in a new array reference forces an update
  }, [entityFormMode]);

  const refreshForm = () => {
    const currFormRef = formRef.current;
    if (currFormRef == null) return;

    // If adding, we use anything supplied in the navState entity onlys
    if (entityFormMode === EntityFormMode.ADD) {
      currFormRef.setFormValues(navState.entityObj);
      return;
    }

    const rowsReq = {};
    const exprs = [ExprUtils.whereParams('id', entityId)];

    // As Of Date
    if (asOfDate) {
      exprs.push(ExprUtils.fieldExpr('verStartInstant', 'IS_LESS', asOfDate));
      exprs.push(ExprUtils.fieldExpr('verEndInstant', 'IS_GREATER_OR_EQUAL', asOfDate));
      rowsReq.hist = true;
    }

    rowsReq.expr = ExprUtils.and(...exprs);

    if (navState.partition) {
      // rowsReq.partition = navState.partition;
    }

    rsrc.one(module, entity, rowsReq).then((entityObj) => {
      entityObj ??= {}; // TODO: What to do if asOfDate is before Entity's creation?
      currFormRef.setFormValues(entityObj);
    });
  };

  // Subscribe to the refresh button, asOfDate, and entityId change (this happens after saving a new Entity)
  useEffect(() => {
    if (fieldDefs == null) return;
    refreshForm();
  }, [refreshTrigger, asOfDate, entityId, fieldDefs]);

  const save = () => {
    if (formRef.current.hasErrorState()) {
      formRef.current.touchAll();
      return;
    }

    const newEntityObj = formRef.current.formValues;

    rsrc.save(module, entity, newEntityObj).then((entityObj) => {
      assign('entityId', entityObj.id);
      setEntityId(entityObj.id);
      setEntityFormMode(EntityFormMode.VIEW);
    });
  };

  const cancel = () => {
    if (entityFormMode === EntityFormMode.ADD) {
      window.history.back();
      return;
    }

    setEntityFormMode(EntityFormMode.VIEW);
    refreshForm();
  };

  if (!fieldDefs) return;

  return (
    <FieldDefsProvider fieldDefs={fieldDefs}>
      <ContentBox className={entityFormMode === EntityFormMode.VIEW ? 'entity-form-wrapper' : null}>
        {/* The className on this box changes the styling of disabled form fields so that in VIEW mode they are more visible */}

        <Form ref={formRef}>{children ?? renderDefaultChildren(fieldDefs)}</Form>

        {entityFormMode !== EntityFormMode.VIEW && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              columnGap: '10px',
            }}
          >
            <Button color="secondary" variant="outlined" sx={{ minWidth: '100px' }} onClick={cancel}>
              <Box component="span" sx={{ mr: 1.3 }}>
                <FontAwesomeIcon icon={faTimes} />
              </Box>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              sx={{ minWidth: '100px' }}
              data-test-id="save-button"
              onClick={save}
            >
              <Box component="span" sx={{ mr: 1.3 }}>
                <FontAwesomeIcon icon={faSave} />
              </Box>
              Save
            </Button>
          </Box>
        )}
      </ContentBox>
    </FieldDefsProvider>
  );
};

const renderDefaultChildren = (fieldDefs) => {
  // Group fieldDefs by section
  const sectionToFieldDefs = {};
  fieldDefs.forEach((fieldDef) => {
    const section = fieldDef.section;
    if (!sectionToFieldDefs[section]) sectionToFieldDefs[section] = [];
    sectionToFieldDefs[section].push(fieldDef);
  });

  // Status and Audit are special and rendered on the right; everything else is stacked on the left
  const leftSections = Object.keys(sectionToFieldDefs).filter((section) => section !== 'Status' && section !== 'Audit');

  return (
    <SpacerLayout minHeight={600}>
      <Spacer weights={[3, 1]}>
        <ScrollableSpacerLayoutArea>
          {leftSections.map((section) => (
            <EntitySectionCard key={section} title={section} />
          ))}
        </ScrollableSpacerLayoutArea>
        <ScrollableSpacerLayoutArea>
          <EntityStatusCard />
          <EntityAuditCard />
        </ScrollableSpacerLayoutArea>
      </Spacer>
    </SpacerLayout>
  );
};
